import styles from "./ClientInfo.module.scss";
import { useNavigate, useParams } from "react-router-dom";
import { ClientsService } from "../../../../services/clients.service";
import React, { useEffect, useRef, useState } from "react";
import { openErrorMessage, openSuccessMessage } from "../../../../store/message/messageSlice";
import { useTranslation } from "react-i18next";
import { IUpdateCustomerModel, IUserForClientsTable } from "../../../../models/User";
import { setProfileImage } from "../../../../helper/imagePaths";
import CustomButton from "../../../../components/ui/CustomButton/CustomButton";
import editIcon from "../../../../assets/images/icons/edit-white.svg";
import FormItemInput from "../../../../components/ui/FormItemInput/FormItemInput";
import { Form, FormInstance } from "antd";
import FormItemDatePicker from "../../../../components/ui/FormItemDatePicker/FormItemDatePicker";
import FormItemSelect from "../../../../components/ui/FormItemSelect/FormItemSelect";
import useAppSelector from "../../../../hooks/useAppSelector";
import dayjs from "dayjs";
import { handleError } from "../../../../helper/errorHandler";
import useAppDispatch from "../../../../hooks/useAppDispatch";
import { IUserSubscription } from "../../../../models/UserSubscription";

const ClientInfo = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const formRef = useRef<FormInstance>(null);

  const { allCountries } = useAppSelector((state) => state.countriesSlice);

  const { id } = useParams();
  const [client, setClient] = useState<IUserForClientsTable | null>(null);
  const [countries, setCountries] = useState<{ id: string; label: string }[]>([]);

  useEffect(() => {
    if (id) getProfileById(id);
  }, [id]);

  useEffect(() => {
    if (formRef.current && client) {
      formRef.current.setFieldsValue({
        firstname: client.firstname,
        lastname: client.lastname,
        phoneNumber: client.contactInformation.phoneNumber,
        phoneCountryCode: client.contactInformation.phoneCountryCode,
        email: client.email,
        dateOfBirth: client.dateOfBirth ? dayjs(client.dateOfBirth) : null,
        gender: client.gender,
      });
    }
  }, [formRef.current, client]);

  useEffect(() => {
    if (allCountries.length > 0) {
      const changedCountries = allCountries
        .map((el) => {
          return {
            id:
              (el.idd.root ? el.idd.root : "+999") +
              (el.idd.suffixes.length ? el.idd.suffixes[0] : ""),
            label:
              (el.idd.root ? el.idd.root : "+999") +
              (el.idd.suffixes.length ? el.idd.suffixes[0] : "") +
              " " +
              el.name.common,
          };
        })
        .sort((a, b) =>
          a.label.toLowerCase() < b.label.toLowerCase()
            ? -1
            : a.label.toLowerCase() > b.label.toLowerCase()
              ? 1
              : 0
        );
      setCountries(changedCountries);
    }
  }, [allCountries]);

  if (!id) {
    navigate("/clients");
    return null;
  }

  const genderItems = [
    { id: "male", label: t("Male") },
    { id: "female", label: t("Female") },
  ];

  const getProfileById = async (userId: string) => {
    try {
      const response = await ClientsService.getClientById(userId);
      if (response.status === 200 && response.data) {
        setClient(response.data);
      } else {
        navigate("/clients");
        dispatch(openErrorMessage(t("SOMETHING_WENT_WRONG")));
      }
    } catch (e) {
      handleError(e, dispatch, t);
      navigate("/clients");
    }
  };

  const onFinish = async (values: IUpdateCustomerModel) => {
    const model: IUpdateCustomerModel = {
      firstname: values.firstname.trim(),
      lastname: values.lastname.trim(),
      phoneCountryCode: values.phoneCountryCode,
      phoneNumber: values.phoneNumber,
      gender: values.gender,
      dateOfBirth: values.dateOfBirth,
    };

    try {
      await ClientsService.updateClient(id, model);
      dispatch(openSuccessMessage(t("ProfileUpdatedSuccessfully")));
      getProfileById(id);
    } catch (e) {
      handleError(e, dispatch, t);
    }
  };

  const renderSubscriptionStatus = (item: IUserSubscription) => {
    const stylesRow = `${styles.status} stripe-status`;
    if (item.isFuture) {
      return <span className={`${stylesRow} future`}>{t("Future")}</span>;
    } else {
      if (item.status === "active") {
        return <span className={`${stylesRow} active`}>{t("Active")}</span>;
      } else if (item.status === "canceled") {
        return <span className={`${stylesRow} canceled`}>{t("Canceled")}</span>;
      } else if (item.status === "incomplete") {
        return <span className={`${stylesRow} incomplete`}>{t("Incomplete")}</span>;
      } else if (item.status === "incomplete_expired") {
        return <span className={`${stylesRow} incomplete_expired`}>{t("IncompleteExpired")}</span>;
      } else if (item.status === "past_due") {
        return <span className={`${stylesRow} past_due`}>{t("PastDue")}</span>;
      } else if (item.status === "trialing") {
        return <span className={`${stylesRow} trialing`}>{t("Trialing")}</span>;
      } else if (item.status === "unpaid") {
        return <span className={`${stylesRow} unpaid`}>{t("Unpaid")}</span>;
      } else {
        return "-";
      }
    }
  };

  if (!client) {
    return null;
  }

  return (
    <div className={styles.page}>
      <Form
        className={styles.form}
        name="edit-client"
        ref={formRef}
        onFinish={onFinish}
        autoComplete={"off"}
        layout={"vertical"}
      >
        <div className={styles.top}>
          <div className={styles.profileInfoWrapper}>
            <div
              className={styles.profileImage}
              style={{ backgroundImage: `url(${setProfileImage(client.profileImagePath)})` }}
            ></div>
            <div className={styles.profileInfo}>
              <p className={styles.name}>
                {client.firstname} {client.lastname}
              </p>
              <p className={styles.idDescription}>{t("ClientID")}:</p>
              <p className={styles.id}>{client.id}</p>
            </div>
          </div>
          <div className={styles.btnWrapper}>
            <CustomButton
              text={t("EditProfile")}
              type={"submit"}
              typeStyle={"fill"}
              icon={editIcon}
              altIcon={t("Edit")}
            />
          </div>
        </div>
        {client.userSubscription && (
          <div className={styles.subscription}>
            <div className={styles.topRow}>
              <p className={styles.text}>{t("SubscriptionDetails")}</p>
              {renderSubscriptionStatus(client.userSubscription)}
            </div>
            <p className={styles.subscriptionInfo}>
              <span className={styles.price}>€{client.userSubscription.subscription.price}</span>
              <span className={styles.credits}>
                {" "}
                / {client.userSubscription.subscription.credits} {t("Credits")}
              </span>
            </p>
            {client.userSubscription.cancelAt && (
              <p className={styles.date}>
                {t("CancelAt")}: {dayjs(client.userSubscription.cancelAt).format("DD/MM/YYYY")}
              </p>
            )}
            {!client.userSubscription.cancelAt && (
              <p className={styles.date}>
                {t("NextPaymentDate")}
                {": "}
                {dayjs(client.userSubscription.currentPeriodEnd).format("DD/MM/YYYY")}
              </p>
            )}
          </div>
        )}

        <div className={styles.table}>
          <FormItemInput
            style={{ gridArea: "item1" }}
            name={"firstname"}
            label={t("FirstName") + " *"}
            rules={{ required: true }}
          />
          <FormItemInput
            style={{ gridArea: "item2" }}
            name={"lastname"}
            label={t("LastName") + " *"}
            rules={{ required: true }}
          />
          <FormItemSelect
            style={{ gridArea: "item3" }}
            showSearch={true}
            name={"phoneCountryCode"}
            label={t("CountryCode") + " *"}
            rules={{ required: true }}
            items={countries}
          />
          <FormItemInput
            style={{ gridArea: "item4" }}
            name={"phoneNumber"}
            label={t("PhoneNumber") + " *"}
            rules={{ required: true }}
            onlyNumber={true}
          />
          <FormItemInput
            style={{ gridArea: "item5" }}
            disabled={true}
            name={"email"}
            label={t("EmailAddress") + " *"}
            rules={{ required: true }}
          />
          <FormItemSelect
            style={{ gridArea: "item6" }}
            name={"gender"}
            label={t("Gender" + " *")}
            rules={{ required: true }}
            items={genderItems}
          />
          <FormItemDatePicker
            style={{ gridArea: "item7" }}
            clearIcon={false}
            dateFormat={"DD/MM/YYYY"}
            name={"dateOfBirth"}
            label={t("Birthday") + " *"}
            rules={{ required: true }}
          />
        </div>
      </Form>
    </div>
  );
};

export default ClientInfo;
